import { AlertTriangle, Check, CircleDashed, ReceiptText, Send, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Captured is only waiting for the card transaction to settle, so it stays neutral rather than warning-coloured.

const EXPENSE_STATE_META: Record<string, { label: string; className?: string; icon?: typeof Check }> = {
  Captured: { label: "Captured", icon: CircleDashed },
  Coded: { label: "Coded", icon: CircleDashed },
  PendingReview: {
    label: "Needs review",
    className: "border-amber-500/30 bg-amber-500/15 text-amber-600",
    icon: ReceiptText,
  },
  Posting: { label: "Posting", icon: Send },
  Posted: {
    label: "Posted",
    className: "border-emerald-500/30 bg-emerald-500/15 text-emerald-600",
    icon: Check,
  },
  Flagged: {
    label: "Flagged",
    className: "border-amber-500/30 bg-amber-500/15 text-amber-600",
    icon: TriangleAlert,
  },
  PostFailed: {
    label: "Post failed",
    className: "border-destructive/30 bg-destructive/15 text-destructive",
    icon: AlertTriangle,
  },
};

export function expenseStateLabel(state?: string | null) {
  if (!state) return null;
  return EXPENSE_STATE_META[state]?.label ?? state;
}

export function ExpenseStateBadge({ state }: { state?: string | null }) {
  const meta = state ? EXPENSE_STATE_META[state] : undefined;
  if (!meta) return <Badge variant="secondary">{state ?? "-"}</Badge>;
  const Icon = meta.icon;
  if (!meta.className) {
    return (
      <Badge variant="secondary">
        {Icon ? <Icon className="size-3" /> : null}
        {meta.label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={meta.className}>
      {Icon ? <Icon className="size-3" /> : null}
      {meta.label}
    </Badge>
  );
}

const TRANSACTION_STATUS_META: Record<string, { label: string; className?: string }> = {
  Pending: { label: "Pending", className: "border-amber-500/30 bg-amber-500/15 text-amber-600" },
  Settled: { label: "Settled", className: "border-emerald-500/30 bg-emerald-500/15 text-emerald-600" },
  Voided: { label: "Voided" },
  Declined: { label: "Declined", className: "border-destructive/30 bg-destructive/15 text-destructive" },
};

export function TransactionStatusBadge({ status }: { status?: string | null }) {
  const meta = status ? TRANSACTION_STATUS_META[status] : undefined;
  if (!meta) return <Badge variant="secondary">{status ?? "-"}</Badge>;
  if (!meta.className) return <Badge variant="secondary">{meta.label}</Badge>;
  return (
    <Badge variant="outline" className={meta.className}>
      {meta.label}
    </Badge>
  );
}

// Written as the thing to do, not the reason code: the queue exists to be cleared.
const FLAG_LABELS: Record<string, string> = {
  AmbiguousMatch: "Pick the right receipt",
  AmountVariance: "Amounts do not agree",
  UnmappedMcc: "Category needs an account",
  MissingAccountingConfiguration: "Accounting setup incomplete",
  AmountChangedAfterPost: "Amount changed after posting",
  TaxRateUnresolved: "Lines need a tax rate",
  PolicyViolation: "Breaks your expense policy",
};

export function flagLabel(reason?: string | null) {
  if (!reason) return null;
  return FLAG_LABELS[reason] ?? reason;
}

// NoSupplierVatNumber is phrased as an action: it is the only blocked reason a person can fix.
const VAT_BLOCKED_LABELS: Record<string, string> = {
  NoReceipt: "No receipt",
  NoSupplierVatNumber: "Receipt has no VAT number",
  NonReclaimableCategory: "Category not reclaimable",
  NoVatCharged: "No VAT charged",
  VatAmountNotItemised: "VAT not itemised",
  PersonalExpense: "Personal spend",
};

export function vatBlockedLabel(reason?: string | null) {
  if (!reason) return null;
  return VAT_BLOCKED_LABELS[reason] ?? reason;
}

export function VatReclaimBadge({
  reclaimable,
  blockedReason,
}: {
  reclaimable?: boolean | null;
  blockedReason?: string | null;
}) {
  if (reclaimable) {
    return (
      <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
        VAT reclaimable
      </Badge>
    );
  }
  const label = vatBlockedLabel(blockedReason);
  if (!label) return <span className="text-xs text-muted-foreground">-</span>;
  // Chaseable blocks read amber (worth acting on); structural ones stay neutral so the queue does not
  // fill with warnings nobody can clear.
  const chaseable = blockedReason === "NoReceipt" || blockedReason === "NoSupplierVatNumber";
  return (
    <Badge
      variant="outline"
      className={chaseable ? "border-amber-500/30 bg-amber-500/15 text-amber-600" : undefined}
    >
      {label}
    </Badge>
  );
}
